<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import LabelPill from 'dashboard/components-next/label/LabelPill.vue';
import DashboardEasyDataTable from 'dashboard/components/table/DashboardEasyDataTable.vue';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['toggleContact', 'selectContact']);

const { t } = useI18n();
const store = useStore();
const accountLabels = useMapGetter('labels/getLabels');
const router = useRouter();
const route = useRoute();

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));
const openActionMenuContactId = ref(null);

const tableHeaders = computed(() => [
  { text: '', value: 'select', sortable: false, width: 52, fixed: true },
  {
    text: t('CONTACTS_LAYOUT.TABLE.NAME'),
    value: 'name',
    sortable: false,
    width: 220,
    fixed: true,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.CHANNELS'),
    value: 'channels',
    sortable: false,
    width: 160,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.LIFECYCLE'),
    value: 'lifecycle',
    sortable: false,
    width: 160,
  },
  { text: t('CONTACTS_LAYOUT.TABLE.EMAIL'), value: 'email', sortable: false },
  { text: t('CONTACTS_LAYOUT.TABLE.PHONE'), value: 'phone', sortable: false },
  {
    text: t('CONTACTS_LAYOUT.TABLE.LABELS'),
    value: 'labels',
    sortable: false,
    width: 192,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.TEAMS'),
    value: 'teams',
    sortable: false,
    width: 172,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.COUNTRY'),
    value: 'country',
    sortable: false,
    width: 140,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.LANGUAGE'),
    value: 'language',
    sortable: false,
    width: 140,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.CONVERSATION_STATUS'),
    value: 'status',
    sortable: false,
    width: 160,
  },
  {
    text: t('CONTACTS_LAYOUT.TABLE.ACTIONS.TITLE'),
    value: 'actions',
    sortable: false,
    width: 72,
  },
]);

const tableRows = computed(() =>
  props.contacts.map(contact => ({
    id: contact.id,
    raw: contact,
  }))
);

const paginationResetKey = computed(() =>
  props.contacts.map(c => c.id).join(',')
);

const headerSelectAll = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(c => selectedIdsSet.value.has(c.id))
);

const headerSelectIndeterminate = computed(() => {
  const n = props.contacts.filter(c => selectedIdsSet.value.has(c.id)).length;
  return n > 0 && n < props.contacts.length;
});

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const onClickViewMessages = id => {
  onClickViewDetails(id);
};

const isSelected = id => selectedIdsSet.value.has(id);
const isPreviewSelected = id => props.selectedContactId === id;

const handleSelect = (id, value) => {
  emit('toggleContact', { id, value });
};

const onClickRow = row => {
  if (row?.raw) emit('selectContact', row.raw);
};

const bodyRowClassName = item => {
  const parts = [];
  if (isSelected(item.id)) parts.push('!bg-n-alpha-2');
  if (isPreviewSelected(item.id)) parts.push('!bg-n-brand/10');
  return parts.join(' ');
};

const dialCodeCountries = computed(() => {
  return [...countries]
    .filter(country => country.dial_code)
    .sort((first, second) => second.dial_code.length - first.dial_code.length);
});

const getCountryFromPhoneNumber = phoneNumber => {
  if (!phoneNumber) return null;

  const phoneNumberWithDialCode = phoneNumber.replace(/[^\d+]/g, '');
  const normalizedPhoneNumber = phoneNumberWithDialCode.startsWith('+')
    ? phoneNumberWithDialCode
    : `+${phoneNumberWithDialCode}`;
  return dialCodeCountries.value.find(country =>
    normalizedPhoneNumber.startsWith(country.dial_code)
  );
};

const getCountryDetails = contact => {
  return getCountryFromPhoneNumber(contact.phoneNumber);
};

const getLanguage = contact => {
  return (
    contact.additionalAttributes?.language || t('CONTACTS_LAYOUT.TABLE.NA')
  );
};

const getLifecycleStage = contact => {
  return contact.lifecycleStage || contact.lifecycle_stage;
};

const getLifecycleDisplay = contact => {
  const v = getLifecycleStage(contact);
  if (!v) return null;
  if (typeof v === 'string') return { icon: '', name: v };
  return v;
};

const resolveContactLabels = contact => {
  const titles = contact.labels || [];
  return accountLabels.value.filter(l => titles.includes(l.title));
};

const getContactTeams = contact => contact.teams || [];

const getContactChannelInboxes = contact => {
  const contactInboxes =
    contact.contactInboxes || contact.contact_inboxes || [];
  return contactInboxes.map(ci => ci.inbox).filter(Boolean);
};

const getConversationStatus = contact => {
  if (contact.blocked) return t('CONTACTS_LAYOUT.TABLE.STATUS.BLOCKED');
  return contact.lastActivityAt
    ? t('CONTACTS_LAYOUT.TABLE.STATUS.ACTIVE')
    : t('CONTACTS_LAYOUT.TABLE.NA');
};

const contactActionItems = contact => [
  {
    label: t('CONTACTS_LAYOUT.TABLE.ACTIONS.VIEW_DETAILS'),
    action: 'view_details',
    value: contact.id,
  },
  {
    label: t('CONTACTS_LAYOUT.TABLE.ACTIONS.VIEW_MESSAGES'),
    action: 'view_messages',
    value: contact.id,
  },
  {
    label: contact.blocked
      ? t('CONTACTS_LAYOUT.TABLE.ACTIONS.UNBLOCK_CONTACT')
      : t('CONTACTS_LAYOUT.TABLE.ACTIONS.BLOCK_CONTACT'),
    action: 'toggle_block',
    value: contact.id,
  },
  {
    label: t('CONTACTS_LAYOUT.TABLE.ACTIONS.DELETE'),
    action: 'delete',
    value: contact.id,
  },
];

const toggleActionMenu = contactId => {
  openActionMenuContactId.value =
    openActionMenuContactId.value === contactId ? null : contactId;
};

const toggleContactBlock = async contact => {
  try {
    await store.dispatch('contacts/update', {
      ...contact,
      blocked: !contact.blocked,
    });
    useAlert(
      contact.blocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      contact.blocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE')
    );
  }
};

const deleteContact = async contact => {
  try {
    await store.dispatch('contacts/delete', contact.id);
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.ERROR_MESSAGE'));
  }
};

const handleContactAction = async (contact, { action }) => {
  openActionMenuContactId.value = null;

  if (action === 'view_details') {
    await onClickViewDetails(contact.id);
  } else if (action === 'view_messages') {
    onClickViewMessages(contact.id);
  } else if (action === 'toggle_block') {
    await toggleContactBlock(contact);
  } else if (action === 'delete') {
    await deleteContact(contact);
  }
};

const onHeaderSelectAll = event => {
  const checked = event.target.checked;
  props.contacts.forEach(contact => handleSelect(contact.id, checked));
};
</script>

<template>
  <div
    class="flex min-h-0 min-w-0 flex-1 flex-col border-t border-n-weak dark:border-n-strong"
  >
    <DashboardEasyDataTable
      fill-available-height
      hide-built-in-footer
      hide-footer-when-empty
      :headers="tableHeaders"
      :items="tableRows"
      :rows-per-page="100"
      :pagination-reset-key="paginationResetKey"
      :body-row-class-name="bodyRowClassName"
      extra-table-class="contacts-easy-data-table"
      extra-header-class="contacts-table-head"
      :empty-message="$t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE')"
      @click-row="onClickRow"
    >
      <template #header-select>
        <div class="flex items-center justify-center px-1" @click.stop>
          <Checkbox
            :model-value="headerSelectAll"
            :indeterminate="headerSelectIndeterminate"
            @change="onHeaderSelectAll"
          />
        </div>
      </template>

      <template #item-select="item">
        <div class="flex items-center justify-center px-1" @click.stop>
          <Checkbox
            :model-value="isSelected(item.id)"
            @change="e => handleSelect(item.id, e.target.checked)"
          />
        </div>
      </template>

      <template #item-name="item">
        <div class="flex min-w-0 items-center gap-3 py-0.5" @click.stop>
          <Avatar
            :name="item.raw.name"
            :src="item.raw.thumbnail"
            :size="32"
            :status="item.raw.availabilityStatus"
            hide-offline-status
            rounded-full
          />
          <span class="min-w-0 truncate font-medium text-n-slate-12">
            {{ item.raw.name || $t('CONTACTS_LAYOUT.TABLE.NA') }}
          </span>
        </div>
      </template>

      <template #item-channels="item">
        <div
          v-if="getContactChannelInboxes(item.raw).length"
          class="flex flex-wrap items-center gap-1.5 py-0.5"
        >
          <span
            v-for="(inbox, idx) in getContactChannelInboxes(item.raw)"
            :key="inbox.id ?? `${item.id}-ch-${idx}`"
            v-tooltip.top="inbox.name || $t('CONTACTS_LAYOUT.TABLE.NA')"
            class="inline-flex shrink-0 cursor-default"
          >
            <InboxChannelGlyph :inbox="inbox" size="sm" />
          </span>
        </div>
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-lifecycle="item">
        <span
          v-if="getLifecycleDisplay(item.raw)"
          class="inline-flex max-w-full items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
        >
          <span v-if="getLifecycleDisplay(item.raw).icon">{{
            getLifecycleDisplay(item.raw).icon
          }}</span>
          <span class="truncate">{{ getLifecycleDisplay(item.raw).name }}</span>
        </span>
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-email="item">
        <span class="line-clamp-2 break-all text-sm text-n-slate-11">
          {{ item.raw.email || $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-phone="item">
        <span class="truncate text-sm text-n-slate-11">
          {{ item.raw.phoneNumber || $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-labels="item">
        <div
          v-if="resolveContactLabels(item.raw).length"
          class="flex flex-wrap gap-1"
        >
          <LabelPill
            v-for="label in resolveContactLabels(item.raw).slice(0, 2)"
            :key="label.id"
            :label="label"
            dense
          />
        </div>
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-teams="item">
        <div
          v-if="getContactTeams(item.raw).length"
          class="flex flex-wrap gap-1"
        >
          <span
            v-for="team in getContactTeams(item.raw).slice(0, 2)"
            :key="team.id"
            class="inline-flex max-w-full items-center rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
          >
            <span class="truncate">{{ team.name }}</span>
          </span>
        </div>
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-country="item">
        <span
          v-if="getCountryDetails(item.raw)"
          class="inline-flex min-w-0 items-center gap-2 text-sm text-n-slate-11"
        >
          <Flag
            :country="getCountryDetails(item.raw).id"
            class="size-4 shrink-0"
          />
          <span class="truncate">{{ getCountryDetails(item.raw).name }}</span>
        </span>
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('CONTACTS_LAYOUT.TABLE.NA') }}
        </span>
      </template>

      <template #item-language="item">
        <span class="truncate text-sm text-n-slate-11">
          {{ getLanguage(item.raw) }}
        </span>
      </template>

      <template #item-status="item">
        <span class="truncate text-sm text-n-slate-11">
          {{ getConversationStatus(item.raw) }}
        </span>
      </template>

      <template #item-actions="item">
        <div class="flex w-full justify-center">
          <div
            v-on-clickaway="() => (openActionMenuContactId = null)"
            class="relative"
            @click.stop
          >
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              variant="ghost"
              size="sm"
              :class="openActionMenuContactId === item.id ? 'bg-n-alpha-2' : ''"
              @click="toggleActionMenu(item.id)"
            />
            <DropdownMenu
              v-if="openActionMenuContactId === item.id"
              :menu-items="contactActionItems(item.raw)"
              class="top-full mt-1 w-40 ltr:right-0 rtl:left-0"
              @action="handleContactAction(item.raw, $event)"
            />
          </div>
        </div>
      </template>
    </DashboardEasyDataTable>
  </div>
</template>
